// S4 (v0.15.0) — Create / edit a role.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useCreateRole,
  usePermissionCatalog,
  useRole,
  useUpdateRole,
} from "@/api/s4";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { QueryState } from "@/components/QueryState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RoleEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  // TWO READS, ONE GATE. `catalog = []` is the dangerous default: a 500 renders
  // the permission section with NOTHING to tick, and an admin saves a role
  // granting nothing while believing they granted what they saw. `existing` is
  // gated with it because on an edit a failed load silently blanks the form —
  // the page then looks like a CREATE and a save would wipe the role's fields.
  const role = useRole(id);
  const perms = usePermissionCatalog();
  const existing = role.data;
  const catalog = perms.data ?? [];
  const create = useCreateRole();
  const update = useUpdateRole(id ?? "");
  const nav = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description ?? "");
      setPermissions(new Set(existing.permissions));
    }
  }, [existing]);

  const isSystem = existing?.is_system_role ?? false;

  const grouped = useMemo(() => {
    const out: Record<string, typeof catalog> = {};
    for (const row of catalog) {
      (out[row.category] = out[row.category] || []).push(row);
    }
    return out;
  }, [catalog]);

  function toggle(key: string) {
    if (isSystem) return;
    setPermissions((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleSave() {
    setError(null);
    const body = {
      name: name.trim(),
      description: description.trim(),
      permissions: Array.from(permissions),
    };
    try {
      if (isEdit) await update.mutateAsync(body);
      else await create.mutateAsync(body);
      nav("/roles");
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed");
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? (isSystem ? "View Role" : "Edit Role") : "Create Custom Role"}
        </h1>
        {isSystem && (
          <p className="mt-1 rounded bg-muted/30 px-3 py-2 text-xs">
            System roles are built-in and cannot be modified.
          </p>
        )}
      </div>
      <QueryState
        isLoading={perms.isLoading || (isEdit && role.isLoading)}
        isError={perms.isError || (isEdit && role.isError)}
        error={perms.error ?? role.error}
        label={isEdit ? "this role and the permission catalog" : "the permission catalog"}
        onRetry={() => {
          void perms.refetch();
          if (isEdit) void role.refetch();
        }}
      >
      <Card>
        <CardContent className="space-y-4 p-4">
          <div>
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSystem}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSystem}
            />
          </div>
          <div className="space-y-3">
            <Label>Permissions</Label>
            {Object.entries(grouped).map(([cat, rows]) => (
              <div key={cat} className="rounded border border-border p-3">
                <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  {cat}
                </p>
                <div className="grid gap-2 md:grid-cols-2">
                  {rows.map((r) => (
                    <label
                      key={r.key}
                      className="flex items-start gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={permissions.has(r.key)}
                        onChange={() => toggle(r.key)}
                        disabled={isSystem}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="font-mono text-xs">{r.key}</span>
                        <span className="block text-xs text-muted-foreground">
                          {r.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {error && (
            <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to="/roles">Cancel</Link>
            </Button>
            {!isSystem && (
              <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
                {isEdit ? "Save Changes" : "Create Role"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      </QueryState>
    </div>
  );
}
